import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase, uploadFile, deleteFile } from './services/supabaseClient';
import { db } from './services/dbService';
import { Navigation } from './components/Navigation';
import { ShopCard } from './components/ShopCard';
import { AIDiagnosticModal } from './components/AIDiagnosticModal';
import { CustomerAISearch } from './components/CustomerAISearch';
import { CustomerReminding } from './components/CustomerReminding';
import { RepairJobCard } from './components/RepairJobCard';
import { LoginScreen } from './components/LoginScreen';
import { CommunityLicense } from './components/CommunityLicense';
import { AdminDashboard } from './components/AdminDashboard';
import { ShopDashboard } from './components/ShopDashboard';
import { BookingDetailPage } from './components/BookingDetailPage';
import { ShopSettings } from './components/ShopSettings';
import { ShopFinance } from './components/ShopFinance';
import { ShopServices } from './components/ShopServices';
import { CustomerProfile } from './components/CustomerProfile';
import { ShopRating } from './components/ShopRating';
import { CheangManual } from './components/CheangManual';
import { Onboarding } from './components/Onboarding';
import { ConfirmModal } from './components/ConfirmModal';
import { Toaster, toast } from 'sonner';
import { SERVICE_CATEGORIES, MOCK_SHOP_ID } from './constants';
import { UserRole, ViewState, Shop, RepairJob, JobStatus, AIDiagnosis, Staff, Service, ChatMessage, WorkTask, Invoice, LocationData, Transaction, Language, Discount, Rating, Reminder, Profile } from './types';
import { LanguageProvider, useTranslation } from './LanguageContext';
import { Search, MapPin, ArrowLeft, Star, Phone, Clock, MessageCircle, Zap, CheckCircle, Sparkles, ClipboardList, Check, Edit3, Tag, Smartphone, Store, Wrench, ShieldCheck, Loader2, Languages } from 'lucide-react';
import { marked } from 'marked';
import { translateText } from './services/geminiService';

const SUPER_ADMIN_EMAIL = 'sengtha@gmail.com';

const HomeView: React.FC<{
  shops: Shop[];
  onShopClick: (s: Shop) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  selectedCategory: string;
  setSelectedCategory: (c: string) => void;
  onOpenAISearch: () => void;
  isAiFiltered: boolean;
  onClearAiFilters: () => void;
}> = ({ shops, onShopClick, searchQuery, setSearchQuery, selectedCategory, setSelectedCategory, onOpenAISearch, isAiFiltered, onClearAiFilters }) => {
  const { language } = useTranslation();
  
  const texts: Record<string, Record<Language, string>> = {
    find_a: { EN: 'Find a', KH: 'ស្វែងរក', ZH: '寻找', JA: '探す', KO: '찾기' },
    cheang: { EN: 'Cheang', KH: 'ជាង', ZH: '技师', JA: '技術者', KO: '기술자' },
    reliable: { 
      EN: 'Reliable repair services across Cambodia, powered by AI diagnostics.',
      KH: 'សេវាកម្មជួសជុលដែលគួរឱ្យទុកចិត្តនៅទូទាំងប្រទេសកម្ពុជា ដំណើរការដោយប្រព័ន្ធរោគវិនិច្ឆ័យ AI។',
      ZH: '柬埔寨各地可靠的维修服务，由 AI 诊断支持。',
      JA: 'カンボジア全土の信頼できる修理サービス。AI 診断でサポート。',
      KO: '캄보디아 전역의 신뢰할 수 있는 수리 서비스. AI 진단으로 구동됩니다.'
    },
    ai_active: { EN: 'AI Diagnostic Active • Clear', KH: 'ការស្វែងរក AI កំពុងដំណើរការ • លុបចេញ', ZH: 'AI 诊断激活 • 清除', JA: 'AI 诊断有効 • クリア', KO: 'AI 진단 활성 • 지우기' },
    search_placeholder: { 
      EN: 'Search electronics, automotive, appliances...',
      KH: 'ស្វែងរកគ្រឿងអេឡិចត្រូនិច, រថយន្ត, គ្រឿងប្រើប្រាស់...',
      ZH: '搜索电子产品、汽车、家电...',
      JA: '電化製品、自動車、家電を検索...',
      KO: '전자 제품, 자동차, 가전 제품 검색...'
    },
    visual_diagnosis: { EN: 'Visual Diagnosis', KH: 'រោគវិនិច្ឆ័យតាមរូបភាព', ZH: '视觉诊断', JA: '画像诊断', KO: '시각적 诊断' },
    match_specialist: { EN: 'AI Match Specialist', KH: 'ស្វែងរកអ្នកជំនាញដោយ AI', ZH: 'AI 匹配专家', JA: 'AI マッチングスペシャリスト', KO: 'AI 매칭 전문가' },
    all_cat: { EN: 'All', KH: 'ទាំងអស់', ZH: '全部', JA: 'すべて', KO: '전체' },
    no_match: { EN: 'No matching specialist found', KH: 'រកមិនឃើញអ្នកជំនាញដែលសមស្រប', ZH: '未找到匹配的专家', JA: '該当する専門家が見つかりません', KO: '일치하는 전문가를 찾을 수 없습니다' },
    try_ai: { EN: 'Try our AI Diagnostic search for precise matching.', KH: 'សាកល្បងការស្វែងរកដោយប្រើ AI ដើម្បីភាពជាក់លាក់។', ZH: '尝试我们的 AI 诊断搜索以进行精确匹配。', JA: 'より正確なマッチングのために AI 诊断をお試しください。', KO: '정확한 매칭을 위해 AI 진단 검색을 시도해 보세요.' }
  };

  const filtered = shops.filter(s => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q) || s.category.some(c => c.toLowerCase().includes(q));
    const matchesCat = selectedCategory === 'All' || s.category.includes(selectedCategory);
    return matchesSearch && matchesCat;
  });

  return (
    <div className="pb-32 pt-24 md:pt-8 px-4 md:px-12 max-w-7xl mx-auto animate-in fade-in duration-500">
      <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-5xl font-black text-slate-900 tracking-tighter mb-4">{texts.find_a[language]} <span className="text-violet-600">{texts.cheang[language]}</span></h1>
          <p className="text-slate-500 font-bold text-lg max-w-xl">{texts.reliable[language]}</p>
        </div>
        {isAiFiltered && (
          <button 
            onClick={onClearAiFilters}
            className="flex items-center gap-2 px-6 py-3 bg-violet-50 text-violet-600 rounded-2xl font-black text-[10px] uppercase tracking-widest border border-violet-100 hover:bg-violet-100 transition-all shadow-sm animate-in slide-in-from-right-4"
          >
            <Check size={14} /> {texts.ai_active[language]}
          </button>
        )}
      </div>

      <div className="mb-10 relative group">
        <div className="flex flex-col md:flex-row gap-4">
           <div className="relative flex-1 group">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-violet-500 transition-colors" size={24} />
              <input 
                 type="text" placeholder={texts.search_placeholder[language]} 
                 className="w-full pl-16 pr-8 py-6 bg-white rounded-[2.2rem] border border-slate-100 shadow-xl shadow-slate-200/50 outline-none focus:ring-4 focus:ring-violet-100 transition-all font-bold text-lg"
                 value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              />
           </div>
           <button 
             onClick={onOpenAISearch}
             className="bg-slate-900 text-white px-8 py-6 rounded-[2.2rem] shadow-xl shadow-slate-900/20 hover:bg-violet-600 transition-all flex items-center justify-center gap-3 group shrink-0 transform active:scale-95"
           >
             <Zap size={24} className="group-hover:scale-110 transition-transform text-amber-400 fill-amber-400" />
             <div className="flex flex-col items-start leading-none">
               <span className="font-black uppercase text-[10px] tracking-widest text-slate-400 group-hover:text-white/70">{texts.visual_diagnosis[language]}</span>
               <span className="font-black uppercase text-xs tracking-widest mt-1">{texts.match_specialist[language]}</span>
             </div>
           </button>
        </div>
      </div>

      <div className="flex items-center gap-3 overflow-x-auto no-scrollbar pb-6 mb-8">
        <button 
          onClick={() => setSelectedCategory('All')}
          className={`px-8 py-4 rounded-[1.4rem] text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all border-2 ${
            selectedCategory === 'All' ? 'bg-slate-900 border-slate-900 text-white shadow-xl' : 'bg-white border-slate-50 text-slate-400 hover:border-slate-200'
          }`}
        >
          {texts.all_cat[language]}
        </button>
        {SERVICE_CATEGORIES.map(cat => (
          <button 
            key={cat} onClick={() => setSelectedCategory(cat)}
            className={`px-8 py-4 rounded-[1.4rem] text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all border-2 ${
              selectedCategory === cat ? 'bg-slate-900 border-slate-900 text-white shadow-xl' : 'bg-white border-slate-50 text-slate-400 hover:border-slate-200'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-10">
        {filtered.length === 0 ? (
          <div className="col-span-full py-32 text-center bg-white rounded-[3rem] border border-slate-50 opacity-40">
             <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                <Search size={32} />
             </div>
             <p className="text-xl font-black text-slate-900">{texts.no_match[language]}</p>
             <p className="text-slate-400 font-bold mt-1">{texts.try_ai[language]}</p>
          </div>
        ) : (
          filtered.map(shop => (
            <ShopCard key={shop.id} shop={shop} onClick={onShopClick} />
          ))
        )}
      </div>
    </div>
  );
};

const BookingsView: React.FC<{
  jobs: RepairJob[];
  userRole: UserRole | null;
  onNavigateToJob: (j: RepairJob) => void;
}> = ({ jobs, userRole, onNavigateToJob }) => {
  const { language } = useTranslation();
  const texts: Record<string, Record<Language, string>> = {
    title: { EN: 'My Repairs', KH: 'ការជួសជុលរបស់ខ្ញុំ', ZH: '我的维修', JA: '修理履歴', KO: '내 수리' },
    sub: { EN: 'Monitor your active fix requests.', KH: 'តាមដានសកម្មភាពជួសជុលរបស់អ្នកទាំងអស់។', ZH: '监控您的有效维修请求。', JA: '現在の修理状況を確認します。', KO: '활성 수리 요청을 모니터링하세요.' },
    empty_title: { EN: 'No repairs yet', KH: 'មិនទាន់មានការជួសជុលនៅឡើយទេ', ZH: '尚无维修记录', JA: '修理履歴はありません', KO: '수리 기록이 없습니다' },
    empty_sub: { EN: 'Start by browsing shops near you.', KH: 'ចាប់ផ្តើមដោយការស្វែងរកហាងដែលនៅជិតអ្នក។', ZH: '从浏览您附近的店铺开始。', JA: '近くのショップを探してみましょう។', KO: '주변 상점을 탐색하여 시작하세요.' }
  };

  return (
    <div className="pb-32 pt-24 md:pt-8 px-4 md:px-12 max-w-7xl mx-auto animate-in fade-in duration-500">
      <div className="mb-12">
        <h1 className="text-4xl font-black text-slate-900 tracking-tighter mb-2">{texts.title[language]}</h1>
        <p className="text-slate-500 font-bold">{texts.sub[language]}</p>
      </div>

      <div className="space-y-6">
        {jobs.length === 0 ? (
          <div className="bg-white rounded-[3rem] p-20 text-center border border-slate-100 shadow-xl shadow-slate-200/50">
            <ClipboardList size={64} className="mx-auto text-slate-100 mb-6" />
            <h3 className="text-2xl font-black text-slate-900 mb-4">{texts.empty_title[language]}</h3>
            <p className="text-slate-400 font-medium mb-10">{texts.empty_sub[language]}</p>
          </div>
        ) : (
          jobs.map(job => (
            <div key={job.id} onClick={() => onNavigateToJob(job)} className="cursor-pointer">
              <RepairJobCard job={job} role={userRole || UserRole.CUSTOMER} />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

function AppContent() {
  const [currentView, setCurrentView] = useState<ViewState>('AUTH');
  const [previousView, setPreviousView] = useState<ViewState>('HOME'); 
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [userAvatar, setUserAvatar] = useState<string | null>(null);
  
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isOnboarding, setIsOnboarding] = useState(false);

  // Core Data Collections
  const [shops, setShops] = useState<Shop[]>([]);
  const [jobs, setJobs] = useState<RepairJob[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [ratings, setRatings] = useState<Rating[]>([]);

  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const [isDiagnosticOpen, setIsDiagnosticOpen] = useState(false);
  const [isAISearchOpen, setIsAISearchOpen] = useState(false);
  const [isAiFiltered, setIsAiFiltered] = useState(false);
  const [isEditingShop, setIsEditingShop] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  
  // Translation state for shop profile
  const [translatedDesc, setTranslatedDesc] = useState<string | null>(null);
  const [isTranslatingDesc, setIsTranslatingDesc] = useState(false);

  const { language } = useTranslation();

  // Reset translation when shop changes
  useEffect(() => {
    setTranslatedDesc(null);
  }, [selectedShop?.id]);

  // Confirmation Modal State
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmLabel?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const requestConfirm = (title: string, message: string, onConfirm: () => void, confirmLabel?: string) => {
    setConfirmDialog({ isOpen: true, title, message, onConfirm, confirmLabel });
  };

  // Centralized Refreshing Logic
  const refreshGlobalData = useCallback(async () => {
    if (!userId || !userRole) return;
    const [fetchedShops, fetchedJobs, fetchedReminders] = await Promise.all([
      db.getShops(),
      db.getRepairJobs(userId, userRole),
      db.getReminders(userId)
    ]);
    setShops(fetchedShops);
    setJobs(fetchedJobs);
    setReminders(fetchedReminders);
  }, [userId, userRole]);

  const refreshShopContext = useCallback(async () => {
    if (!userId || !userRole) return;
    let shopIdToFetch: string | undefined;
    
    if (userRole === UserRole.SHOP_OWNER) {
      const myShopRecord = shops.find(s => s.owner_id === userId);
      shopIdToFetch = myShopRecord?.id;
    } else if (userRole === UserRole.STAFF && userEmail) {
      const { data: staffRec } = await supabase
        .from('staff')
        .select('shop_id')
        .eq('email', userEmail)
        .maybeSingle();
      shopIdToFetch = staffRec?.shop_id;
    }

    if (shopIdToFetch) {
      const [s, sv, t, d, r] = await Promise.all([
        db.getStaff(shopIdToFetch),
        db.getServices(shopIdToFetch),
        db.getTransactions(shopIdToFetch),
        db.getDiscounts(shopIdToFetch),
        db.getRatings(shopIdToFetch)
      ]);
      setStaff(s);
      setServices(sv);
      setTransactions(t);
      setDiscounts(d);
      setRatings(r);
    }
  }, [userId, userRole, userEmail, shops]);

  // Supabase Auth Listener
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) handlePostAuth(session.user);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) handlePostAuth(session.user);
      else {
        setUserId(null);
        setUserRole(null);
        setCurrentView('AUTH');
        setIsOnboarding(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handlePostAuth = async (user: any) => {
    setUserId(user.id);
    setUserEmail(user.email);
    setUserName(user.user_metadata.full_name || user.email);
    setUserAvatar(user.user_metadata.avatar_url);
    setIsLoadingProfile(true);

    try {
      const profile = await db.getProfile(user.id);
      if (profile) {
        setUserRole(profile.role);
        setIsOnboarding(false);
        if (profile.role === UserRole.ADMIN) setCurrentView('ADMIN_DASHBOARD');
        else if (profile.role === UserRole.SHOP_OWNER || profile.role === UserRole.STAFF) setCurrentView('SHOP_DASHBOARD');
        else setCurrentView('HOME');
      } else {
        if (user.email === SUPER_ADMIN_EMAIL) {
          await db.createProfile({
            id: user.id,
            role: UserRole.ADMIN,
            full_name: user.user_metadata.full_name || 'Super Admin',
            email: user.email,
            avatar_url: user.user_metadata.avatar_url
          });
          setUserRole(UserRole.ADMIN);
          setCurrentView('ADMIN_DASHBOARD');
        } else {
          setIsOnboarding(true);
          setCurrentView('ONBOARDING');
        }
      }
    } catch (err) {
      setIsOnboarding(true);
      setCurrentView('ONBOARDING');
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handleOnboardingComplete = (profile: Profile) => {
    setUserRole(profile.role);
    setIsOnboarding(false);
    if (profile.role === UserRole.ADMIN) setCurrentView('ADMIN_DASHBOARD');
    else if (profile.role === UserRole.SHOP_OWNER || profile.role === UserRole.STAFF) setCurrentView('SHOP_DASHBOARD');
    else setCurrentView('HOME');
  };

  useEffect(() => {
    if (userId && userRole && currentView !== 'AUTH') {
      refreshGlobalData();
    }
  }, [userId, userRole, currentView, refreshGlobalData]);

  useEffect(() => {
    if (userId && userRole && (currentView === 'SHOP_DASHBOARD' || currentView === 'SHOP_PROFILE' || currentView === 'BOOKING_DETAIL')) {
      refreshShopContext();
    }
  }, [userId, userRole, currentView, shops.length, refreshShopContext]);

  // Mutation Handlers with automatic sync
  const handleBookingConfirm = async (data: any) => {
    if (!selectedShop || !userId) return;
    try {
      // Explicitly map properties to ensure dbService receives expected names
      await db.createRepairJob({
        shopId: selectedShop.id,
        shopName: selectedShop.name,
        customerId: userId,
        customerName: userName || 'Client',
        ...data
      });
      await refreshGlobalData();
      setIsDiagnosticOpen(false);
      setSelectedShop(null);
      setCurrentView('BOOKINGS');
      toast.success("Repair request submitted!");
    } catch (err: any) {
      console.error("Booking error:", err);
      toast.error(`Booking failed: ${err.message || 'Check connection'}`);
    }
  };

  const handleStatusUpdate = async (jobId: string, status: JobStatus) => {
    await db.updateJobStatus(jobId, status);
    await refreshGlobalData();
    toast.success(`Updated to ${status}`);
  };

  const handleAddStaff = async (s: Staff) => {
    await db.createStaff(s);
    await refreshShopContext();
    toast.success("Staff added.");
  };

  const handleRemoveStaff = async (id: string) => {
    const s = staff.find(item => item.id === id);
    requestConfirm("Remove Staff?", `Delete ${s?.name}? This also wipes their photo.`, async () => {
      if (s?.avatarUrl) await deleteFile(s.avatarUrl);
      await db.deleteStaff(id);
      await refreshShopContext();
      toast.success("Staff removed.");
    });
  };

  const handleAddService = async (s: Service) => {
    await db.createService(s);
    await refreshShopContext();
    toast.success("Service published.");
  };

  const handleUpdateService = async (s: Service) => {
    await db.updateService(s.id, s);
    await refreshShopContext();
    toast.success("Service updated.");
  };

  const handleRemoveService = async (id: string) => {
    const s = services.find(item => item.id === id);
    requestConfirm("Delete Service?", `Remove ${s?.name}?`, async () => {
      if (s?.imageUrl) await deleteFile(s.imageUrl);
      await db.deleteService(id);
      await refreshShopContext();
      toast.success("Service deleted.");
    });
  };

  const handleAddTransaction = async (t: Transaction) => {
    await db.createTransaction(t);
    await refreshShopContext();
    toast.success("Record saved.");
  };

  const handleAddDiscount = async (d: Discount) => {
    await db.createDiscount(d);
    await refreshShopContext();
    toast.success("Campaign launched.");
  };

  const handleRemoveDiscount = async (id: string) => {
    await db.deleteDiscount(id);
    await refreshShopContext();
    toast.success("Campaign removed.");
  };

  const handleToggleDiscount = async (id: string) => {
    const current = discounts.find(d => d.id === id);
    if (!current) return;
    await db.toggleDiscount(id, current.status);
    await refreshShopContext();
  };

  const handleAddRating = async (jobId: string, shopId: string, stars: number, comment: string) => {
    if (!userId || !userName) return;
    await db.createRating({ jobId, shopId, stars, comment, customerId: userId, customerName: userName });
    await refreshGlobalData();
    await refreshShopContext();
    toast.success("Rating submitted.");
  };

  const handleTranslateShopDesc = async (desc: string) => {
    if (translatedDesc) {
      setTranslatedDesc(null);
      return;
    }
    setIsTranslatingDesc(true);
    try {
      const result = await translateText(desc, language);
      setTranslatedDesc(result);
    } catch (error) {
      console.error("Translation failed", error);
      toast.error("Translation failed. Try again later.");
    } finally {
      setIsTranslatingDesc(false);
    }
  };

  const myShop = useMemo(() => {
    if (userRole === UserRole.SHOP_OWNER) return shops.find(s => s.owner_id === userId);
    if (userRole === UserRole.STAFF && staff.length > 0 && userEmail) {
      return shops.find(s => staff.some(st => st.shopId === s.id && st.email === userEmail));
    }
    return undefined;
  }, [shops, userId, userRole, staff, userEmail]);

  return (
    <div className="min-h-screen bg-transparent text-slate-900 font-sans selection:bg-violet-200 selection:text-violet-900">
      <Toaster position="top-right" richColors closeButton />
      <ConfirmModal 
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
      />
      
      {!isOnboarding && currentView !== 'AUTH' && currentView !== 'COMMUNITY_LICENSE' && (
        <Navigation 
          currentView={currentView} 
          onNavigate={(v) => { setPreviousView(currentView); setCurrentView(v); setIsEditingShop(false); }} 
          userRole={userRole || UserRole.CUSTOMER} 
          onLogout={async () => await supabase.auth.signOut()} 
          isSuperAdmin={userEmail === SUPER_ADMIN_EMAIL}
          onSwitchRole={() => {
            const roles = [UserRole.CUSTOMER, UserRole.SHOP_OWNER, UserRole.STAFF, UserRole.ADMIN];
            const next = roles[(roles.indexOf(userRole!) + 1) % roles.length];
            setUserRole(next);
            if (next === UserRole.ADMIN) setCurrentView('ADMIN_DASHBOARD');
            else if (next === UserRole.SHOP_OWNER || next === UserRole.STAFF) setCurrentView('SHOP_DASHBOARD');
            else setCurrentView('HOME');
          }}
        />
      )}

      <main className={`min-h-screen transition-all duration-500 ${!isOnboarding && currentView !== 'AUTH' && currentView !== 'COMMUNITY_LICENSE' ? 'md:pl-72' : ''}`}>
         {currentView === 'AUTH' && <LoginScreen onShowLicense={() => setCurrentView('COMMUNITY_LICENSE')} onShowManual={() => setCurrentView('MANUAL')} />}
         {currentView === 'COMMUNITY_LICENSE' && <CommunityLicense onBack={() => setCurrentView(previousView)} />}
         {currentView === 'ONBOARDING' && userId && <Onboarding userId={userId} email={userEmail!} initialName={userName!} onComplete={handleOnboardingComplete} onLogout={async () => await supabase.auth.signOut()} />}

         {!isOnboarding && currentView !== 'ONBOARDING' && (
           <>
             {currentView === 'HOME' && <HomeView shops={shops} onShopClick={s => { setSelectedShop(s); setCurrentView('SHOP_PROFILE'); }} searchQuery={searchQuery} setSearchQuery={setSearchQuery} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} onOpenAISearch={() => setIsAISearchOpen(true)} isAiFiltered={isAiFiltered} onClearAiFilters={() => { setSearchQuery(''); setSelectedCategory('All'); setIsAiFiltered(false); }} />}
             {currentView === 'BOOKINGS' && <BookingsView jobs={jobs} userRole={userRole} onNavigateToJob={j => { setSelectedJobId(j.id); setCurrentView('BOOKING_DETAIL'); }} />}
             {currentView === 'REMINDERS' && userId && (
                <CustomerReminding 
                  reminders={reminders} 
                  onAdd={async rs => { await db.saveReminders(rs.map(r => ({ ...r, customerId: userId }))); await refreshGlobalData(); toast.success("Reminders added."); }}
                  onDelete={async id => requestConfirm("Delete Reminder?", "Confirm removal.", async () => { await db.deleteReminder(id); await refreshGlobalData(); toast.success("Deleted."); })}
                  onToggleComplete={async id => { const r = reminders.find(x => x.id === id); if(r) { await db.toggleReminder(id, !r.isCompleted); await refreshGlobalData(); } }}
                  onSearchShop={k => { setSearchQuery(k); setCurrentView('HOME'); }}
                />
             )}
             {currentView === 'SHOP_PROFILE' && (
                <div className="pb-32 pt-24 md:pt-8 px-4 md:px-12 max-w-7xl mx-auto">
                   {(() => {
                     const s = selectedShop || myShop;
                     if (!s) return <div className="text-center py-20 text-slate-400 font-bold uppercase tracking-widest">No Shop Selected</div>;
                     return isEditingShop ? <ShopSettings shop={s} onUpdateShop={async updated => { await db.updateShop(updated.id, updated); await refreshGlobalData(); setIsEditingShop(false); toast.success("Settings saved."); }} /> : (
                       <div className="space-y-8">
                         <div className="flex justify-between items-center"><button onClick={() => setCurrentView(previousView)} className="flex items-center text-slate-400 font-bold hover:text-slate-900 transition-colors"><ArrowLeft size={20} className="mr-2"/> Back</button>{s.owner_id === userId && <button onClick={() => setIsEditingShop(true)} className="px-6 py-3 bg-white border rounded-2xl font-black text-[10px] uppercase text-slate-600 hover:text-violet-600 flex items-center gap-2"><Edit3 size={14} /> Edit Info</button>}</div>
                         <div className="bg-white rounded-[3.5rem] overflow-hidden border shadow-2xl">
                            <div className="h-80 relative"><img src={s.imageUrl} className="w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 flex items-end p-12"><div className="flex flex-col md:flex-row items-center gap-8 w-full"><img src={s.logoUrl} className="w-32 h-32 rounded-[2.5rem] bg-white p-4 shadow-2xl" /><div className="text-white"><div className="flex items-center gap-4 mb-2"><h1 className="text-5xl font-black">{s.name}</h1><div className="bg-white/20 px-3 py-1 rounded-xl flex items-center gap-1.5"><Star size={14} className="text-amber-400 fill-amber-400" /><span className="text-sm font-black">{s.rating}</span></div></div><div className="flex gap-6 text-sm font-bold opacity-80"><div className="flex items-center gap-2"><MapPin size={16}/> {s.address}</div><div className="flex items-center gap-2"><Clock size={16}/> {s.workingHours}</div></div></div></div></div></div>
                            <div className="p-12">
                               <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                                 <div className="lg:col-span-2 space-y-12">
                                   <section>
                                     <div className="flex items-center justify-between mb-6">
                                       <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">About</h3>
                                       <button 
                                         onClick={() => handleTranslateShopDesc(s.description)}
                                         disabled={isTranslatingDesc}
                                         className="flex items-center gap-2 px-4 py-2 bg-slate-50 text-violet-600 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-violet-50 transition-all"
                                       >
                                         {isTranslatingDesc ? <Loader2 size={12} className="animate-spin" /> : <Languages size={12} />}
                                         {translatedDesc ? 'Show Original' : 'Translate'}
                                       </button>
                                     </div>
                                     <div 
                                       className="markdown-content text-slate-600 text-lg leading-relaxed animate-in fade-in"
                                       dangerouslySetInnerHTML={{ __html: marked.parse(translatedDesc || s.description || '') }}
                                     />
                                   </section>
                                 </div>
                                 <div className="space-y-8">
                                   {s.owner_id !== userId && userRole === UserRole.CUSTOMER && (
                                     <div className="bg-slate-900 rounded-[3rem] p-10 text-white shadow-2xl">
                                       <h3 className="text-xl font-black mb-4">Request a Fix</h3>
                                       <p className="text-slate-400 text-sm font-bold mb-8">Get an AI-powered estimate instantly.</p>
                                       <button onClick={() => setIsDiagnosticOpen(true)} className="w-full py-6 bg-violet-600 rounded-2xl font-black uppercase flex items-center justify-center gap-3"><Zap size={20} fill="currentColor" /> Start AI Booking</button>
                                     </div>
                                   )}
                                 </div>
                               </div>
                            </div>
                         </div>
                       </div>
                     );
                   })()}
                </div>
             )}
             {(currentView === 'SHOP_DASHBOARD' || currentView === 'SHOP_SERVICES' || currentView === 'SHOP_FINANCE') && userRole && <ShopDashboard userRole={userRole} shop={myShop} jobs={jobs} staff={staff} services={services} transactions={transactions} discounts={discounts} ratings={ratings} initialTab={currentView === 'SHOP_SERVICES' ? 'SERVICES' : currentView === 'SHOP_FINANCE' ? 'FINANCE' : 'OVERVIEW'} onStatusChange={handleStatusUpdate} onAddStaff={handleAddStaff} onRemoveStaff={handleRemoveStaff} onAddService={handleAddService} onUpdateService={handleUpdateService} onRemoveService={handleRemoveService} onAddTransaction={handleAddTransaction} onAddDiscount={handleAddDiscount} onDeleteDiscount={handleRemoveDiscount} onToggleDiscountStatus={handleToggleDiscount} onSendMessage={async (jid, text) => { const job = jobs.find(x => x.id === jid); if(!job) return; const isShopSide = userRole === UserRole.SHOP_OWNER || userRole === UserRole.STAFF; const msg = { id: `m-${Date.now()}`, senderId: userId!, senderRole: userRole, senderName: userName!, text, timestamp: new Date().toISOString(), isShopSide }; await db.updateJob(jid, { messages: [...(job.messages || []), msg] }); await refreshGlobalData(); }} onNavigateToJob={j => { setSelectedJobId(j.id); setCurrentView('BOOKING_DETAIL'); }} />}
             {currentView === 'BOOKING_DETAIL' && selectedJobId && <BookingDetailPage job={jobs.find(j => j.id === selectedJobId)!} shop={shops.find(s => s.id === jobs.find(j => j.id === selectedJobId)!.shopId)} currentUserId={userId!} currentUserName={userName!} currentUserRole={userRole!} allStaff={staff} allServices={services} allRatings={ratings} allDiscounts={discounts} allJobs={jobs} onStatusUpdate={handleStatusUpdate} onFinalizeSchedule={async (jid, s) => { await db.updateJob(jid, { scheduledDate: s, status: JobStatus.CONFIRMED }); await refreshGlobalData(); toast.success("Scheduled."); }} onAssignStaff={async (jid, sids) => { await db.updateJob(jid, { assignedStaffIds: sids }); await refreshGlobalData(); }} onSendMessage={async (jid, text) => { const j = jobs.find(x => x.id === jid); if(!j) return; const msg = { id: `m-${Date.now()}`, senderId: userId!, senderRole: userRole!, senderName: userName!, text, timestamp: new Date().toISOString(), isShopSide: userRole !== UserRole.CUSTOMER }; await db.updateJob(jid, { messages: [...(j.messages || []), msg] }); await refreshGlobalData(); }} onAddTask={async (jid, t) => { const j = jobs.find(x => x.id === jid); if(!j) return; await db.updateJob(jid, { workTasks: [...(j.workTasks || []), t] }); await refreshGlobalData(); }} onCreateInvoice={async (jid, inv) => { await db.updateJob(jid, { invoice: inv }); await refreshGlobalData(); }} onUploadProof={async (jid, img) => { await db.updateJob(jid, { paymentProof: { imageUrl: img, submittedAt: new Date().toISOString(), status: 'PENDING' } }); await refreshGlobalData(); }} onVerifyPayment={async jid => { const j = jobs.find(x => x.id === jid); if(!j) return; await db.updateJob(jid, { paymentProof: { ...j.paymentProof!, status: 'VERIFIED' }, invoice: { ...j.invoice!, status: 'PAID' }, status: JobStatus.COMPLETED }); await refreshGlobalData(); toast.success("Verified!"); }} onAddRating={handleAddRating} onBack={() => setCurrentView(previousView)} />}
             {currentView === 'ADMIN_DASHBOARD' && userId && <AdminDashboard onLogout={async () => await supabase.auth.signOut()} userId={userId} isSuperAdmin={userEmail === SUPER_ADMIN_EMAIL} onSwitchRole={() => { const roles = [UserRole.CUSTOMER, UserRole.SHOP_OWNER, UserRole.STAFF, UserRole.ADMIN]; setUserRole(roles[(roles.indexOf(userRole!) + 1) % roles.length]); }} />}
             {currentView === 'PROFILE' && <CustomerProfile onLogout={async () => await supabase.auth.signOut()} isSuperAdmin={userEmail === SUPER_ADMIN_EMAIL} onSwitchRole={() => { const roles = [UserRole.CUSTOMER, UserRole.SHOP_OWNER, UserRole.STAFF, UserRole.ADMIN]; setUserRole(roles[(roles.indexOf(userRole!) + 1) % roles.length]); }} />}
             {currentView === 'MANUAL' && <div className="relative">{!userId && <button onClick={() => setCurrentView('AUTH')} className="fixed top-6 left-6 z-[100] px-6 py-3 bg-white/10 text-white rounded-2xl font-black uppercase tracking-widest"><ArrowLeft size={14} /> Back</button>}<CheangManual /></div>}
           </>
         )}
      </main>
      
      {(selectedShop || (userRole === UserRole.SHOP_OWNER && myShop)) && isDiagnosticOpen && <AIDiagnosticModal isOpen={isDiagnosticOpen} onClose={() => setIsDiagnosticOpen(false)} shop={selectedShop || myShop!} onConfirmBooking={handleBookingConfirm} />}
      <CustomerAISearch isOpen={isAISearchOpen} onClose={() => setIsAISearchOpen(false)} onApplyResults={(c, k) => { setSearchQuery(k); setSelectedCategory(c); setIsAiFiltered(true); }} />

      {isLoadingProfile && <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-slate-900/60 backdrop-blur-md"><div className="w-16 h-16 bg-white rounded-3xl flex items-center justify-center shadow-2xl mb-4 animate-bounce"><Loader2 size={32} className="text-violet-600 animate-spin" /></div><p className="text-white font-black uppercase text-[10px] tracking-[0.3em]">Checking Access...</p></div>}
    </div>
  );
}